import React, { useEffect, useState } from 'react';

// 타이핑 효과를 위한 커스텀 훅 (동적으로 텍스트가 바뀔 때마다 사용 가능하도록 개선)
function useTypewriter(text, start, speed = 30, onDone) {
  const [display, setDisplay] = useState('');
  useEffect(() => {
    if (!start || !text) return;
    setDisplay('');
    let i = 0;
    const interval = setInterval(() => {
      setDisplay(text.slice(0, i + 1));
      i++;
      if (i >= text.length) {
        clearInterval(interval);
        if (onDone) onDone();
      }
    }, speed);
    return () => clearInterval(interval);
  }, [text, start, speed, onDone]);
  return display;
}

const botMsg1 = '안녕하세요! GenAI 챗봇입니다. 무엇을 도와드릴까요?';
const userMsg = 'Generative AI가 정확히 무엇인가요?';
const botMsg2 = 'Generative AI는 대량의 데이터를 학습하여 새로운 콘텐츠를 만들어내는 인공지능입니다. 텍스트, 이미지, 코드 등 다양한 형태의 콘텐츠를 생성할 수 있습니다.';

// 헤드라인 단어와 설명 문구
const heroContents = [
  {
    word: '혁신',
    desc: 'AI 기술을 통해 기업과 개인의 일상에 스마트하고 따뜻한 변화를 만들어갑니다. 기존의 방식에 얽매이지 않고, 새로운 관점으로 문제를 해결합니다. GenAI Dev는 기술의 가능성을 현실로 바꾸는 혁신 파트너입니다.'
  },
  {
    word: '자동화',
    desc: '반복적인 업무를 AI가 대신 처리하여 더 가치 있는 일에 집중할 수 있게 합니다. 수작업으로 처리하던 보고서, 데이터 입력, 고객 응대를 자동화하여 업무 효율을 극대화합니다. 사람은 창의적인 일에, AI는 반복적인 일에 집중하는 환경을 만들어드립니다.'
  },
  {
    word: '연결',
    desc: '사람과 기술, 데이터를 연결하여 비즈니스의 새로운 가능성을 열어갑니다. 분절된 시스템과 정보를 하나로 통합하여 조직 전체가 같은 그림을 볼 수 있게 합니다. 부서 간, 시스템 간 경계를 허물고 원활한 협업 환경을 구축합니다.'
  },
  {
    word: '설계',
    desc: '비즈니스 목표에 맞춘 최적의 시스템을 기획부터 운영까지 설계합니다. 단순히 기능을 나열하는 것이 아니라, 실제 사용자의 업무 흐름을 분석하여 최적의 구조를 제안합니다. 확장성과 유지보수성을 고려한 견고한 아키텍처를 설계합니다.'
  },
  {
    word: '최적화',
    desc: '데이터 기반 분석으로 업무 프로세스를 효율적으로 개선합니다. 병목 구간을 찾아내고, 불필요한 단계를 제거하여 업무 속도를 높입니다. 지속적인 모니터링과 개선을 통해 시스템 성능을 최상의 상태로 유지합니다.'
  },
  {
    word: '구축',
    desc: '아이디어를 실제로 동작하는 서비스로 만들어 성과를 창출합니다. 기획 단계의 아이디어가 실제 사용자에게 전달되기까지 모든 과정을 책임집니다. 안정적인 배포와 운영 환경을 구축하여 서비스가 중단 없이 동작하도록 보장합니다.'
  },
  {
    word: '분석',
    desc: 'AI 기반 데이터 분석으로 의사결정의 속도와 정확도를 높입니다. 흩어진 데이터를 수집하고 정제하여 의미 있는 인사이트를 도출합니다. 실시간 대시보드와 자동 리포트로 경영진이 빠르게 상황을 파악하고 대응할 수 있게 합니다.'
  },
  {
    word: '성장',
    desc: '비즈니스 성장을 가속화하는 맞춤형 AI 솔루션을 제공합니다. 현재의 규모에 맞추되, 미래의 확장을 고려한 시스템을 설계합니다. 고객사의 성장 단계에 맞춰 유연하게 대응하며 함께 성장하는 파트너가 되겠습니다.'
  },
  {
    word: '간소화',
    desc: '복잡한 업무 프로세스를 단순하고 직관적으로 바꿔드립니다. 사용자가 시스템을 배우는 데 시간을 쓰지 않도록, 직관적인 UI/UX를 설계합니다. 클릭 한 번으로 처리할 수 있는 일에 여러 단계를 거치지 않도록 업무 흐름을 개선합니다.'
  },
  {
    word: '실현',
    desc: '"만드는 것"에서 끝나지 않고 "돌아가게 만드는 것"까지 책임집니다. 개발 완료가 프로젝트의 끝이 아니라 시작이라고 생각합니다. 실제 운영 환경에서 발생하는 이슈에 빠르게 대응하고, 지속적인 개선을 통해 서비스 품질을 높여갑니다.'
  },
];

const HeroSection = () => {
  // 슬롯머신 스타일 헤드라인
  const [heroIndex, setHeroIndex] = useState(0);
  const [isSpinning, setIsSpinning] = useState(true);
  const [showDesc, setShowDesc] = useState(false);

  useEffect(() => {
    let spinInterval;
    let spinTimeout;
    let pauseTimeout;

    const startSpin = () => {
      setShowDesc(false);
      setIsSpinning(true);

      // 단어 변경 (150ms 간격)
      spinInterval = setInterval(() => {
        setHeroIndex(Math.floor(Math.random() * heroContents.length));
      }, 150);

      // 1.5~2.5초 후 랜덤하게 멈춤
      const spinDuration = 1500 + Math.random() * 1000;
      spinTimeout = setTimeout(() => {
        clearInterval(spinInterval);
        // 최종 랜덤 인덱스 선택
        const finalIndex = Math.floor(Math.random() * heroContents.length);
        setHeroIndex(finalIndex);
        setIsSpinning(false);

        // 멈춘 후 설명 문구 표시
        setTimeout(() => setShowDesc(true), 200);

        // 3초 후 다시 스핀 시작
        pauseTimeout = setTimeout(startSpin, 3000);
      }, spinDuration);
    };

    startSpin();

    return () => {
      clearInterval(spinInterval);
      clearTimeout(spinTimeout);
      clearTimeout(pauseTimeout);
    };
  }, []);

  // 대화 단계: 0=챗봇1, 1=유저타이핑, 2=유저질문, 3=챗봇로딩, 4=챗봇답변, 5=실제 대화
  const [step, setStep] = useState(0);
  const [messages, setMessages] = useState([
    { role: 'bot', content: botMsg1 },
  ]);

  // 2026-08-21: 실시간 챗 기능을 걷어냈다.
  //   여기서 부르던 GET /api/chat/history 와 POST /api/chat 의 백엔드(api.genaidev.io ·
  //   genai-backend)가 MySQL 인증 실패로 2026-05 이후 줄곧 500 이다. 저널을 뒤져 보면
  //   그 기간 성공 응답이 한 건도 없다. 게다가 CORS allow_origins 에 genaidev.io 가 빠져
  //   있어, 방문자가 입력창에 무언가 쳐도 돌아오는 건 "서버와의 연결에 문제가 있습니다."
  //   뿐이었다. 고장난 입구를 회사 첫 화면에 열어 두는 것보다 닫는 쪽을 택했다.
  //
  //   아래 예시 대화는 네트워크를 타지 않는 순수 연출이라 그대로 둔다 — 히어로의
  //   시각적 중심이고, 고장날 구석이 없다.
  //
  //   되살릴 때 필요한 것: (1) genai DB 자격증명 복구, (2) allow_origins 에
  //   https://genaidev.io 추가, (3) 공개 엔드포인트인 만큼 인증·레이트리밋.
  //   경위와 근거는 ~/PORTS.md 의 api.genaidev.io 절에 적어 두었다.

  // 예시 타이핑 효과
  const userTyped = useTypewriter(userMsg, step >= 2 && step < 5, 40);
  const botTyped2 = useTypewriter(botMsg2, step === 4, 25);

  // 예시 대화 단계별 진행
  useEffect(() => {
    if (step === 0) {
      setTimeout(() => setStep(1), 1200); // 챗봇1 → 유저타이핑
    } else if (step === 1) {
      setTimeout(() => setStep(2), 700); // 유저 ... → 유저질문
    } else if (step === 2) {
      setTimeout(() => setStep(3), 1200); // 유저질문 → 챗봇로딩
    } else if (step === 3) {
      setTimeout(() => setStep(4), 1000); // 챗봇로딩 → 챗봇답변
    } else if (step === 4) {
      // 예시 대화 내역에 추가 후 실제 대화 모드로 전환
      setTimeout(() => {
        setMessages([
          { role: 'bot', content: botMsg1 },
          { role: 'user', content: userMsg },
          { role: 'bot', content: botMsg2 },
        ]);
        setStep(5);
      }, 1200);
    }
  }, [step]);

  return (
    <section className="relative min-h-screen flex items-center bg-gradient-to-br from-light to-white overflow-hidden">
      {/* 배경 그래픽 요소 */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/3 left-1/3 w-80 h-80 bg-secondary/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/4 w-72 h-72 bg-accent/10 rounded-full blur-3xl"></div>
      </div>
      <div className="container mx-auto px-4 z-10 pt-24">
        <div className="flex flex-col md:flex-row items-center">
          <div className="md:w-1/2 mb-12 md:mb-0">
            <span className="inline-block px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium mb-6">
              Generative AI 솔루션
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-dark leading-tight mb-6">
              Generative AI로 일상을 <br />
              <span className="inline-flex items-baseline">
                <span
                  className={`inline-block min-w-[140px] md:min-w-[180px] text-center border-b-4 border-dark pb-1 ${isSpinning ? 'text-gray-400' : 'text-primary'}`}
                >
                  {heroContents[heroIndex].word}
                </span>
                <span className="ml-1">하다</span>
              </span>
            </h1>
            <div className="min-h-[120px] mb-8 max-w-lg">
              <p className={`text-lg text-gray-600 transition-all duration-300 ${showDesc ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
                {heroContents[heroIndex].desc}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="#services" 
                className="bg-primary text-white px-6 py-3 rounded-md text-center font-medium hover:bg-primary/90 transition-colors"
              >
                서비스 알아보기
              </a>
              <a 
                href="#contact" 
                className="border-2 border-primary text-primary px-6 py-3 rounded-md text-center font-medium hover:bg-primary/5 transition-colors"
              >
                문의하기
              </a>
            </div>
          </div>
          <div className="md:w-1/2 relative">
            <div className="relative z-10 bg-white p-4 md:p-8 rounded-2xl shadow-xl min-h-[340px] flex flex-col justify-end">
              {/* 챗봇 인터페이스 */}
              <div className="bg-light rounded-lg p-4 min-h-[220px] flex flex-col justify-end max-h-[60vh] overflow-y-auto custom-scrollbar">
                {/* 예시 대화 단계 */}
                {step < 5 ? (
                  <>
                    {/* 챗봇 메시지 1 */}
                    <div className="flex items-start mb-4">
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center mr-3 flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                        </svg>
                      </div>
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <p className="text-gray-700">{botMsg1}</p>
                      </div>
                    </div>
                    {/* 유저 타이핑/질문 */}
                    {step >= 1 && (
                      <div className="flex items-start mb-4 justify-end">
                        <div className="bg-primary/10 p-3 rounded-lg min-w-[120px] min-h-[24px] flex items-center">
                          {step === 1 && <span className="text-gray-400 animate-pulse">...</span>}
                          {step >= 2 && <span className="text-gray-700">{userTyped}</span>}
                        </div>
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center ml-3 flex-shrink-0">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                      </div>
                    )}
                    {/* 챗봇 로딩/답변 */}
                    {step >= 3 && (
                      <div className="flex items-start">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center mr-3 flex-shrink-0">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                          </svg>
                        </div>
                        <div className="bg-white p-3 rounded-lg shadow-sm min-w-[180px] min-h-[24px]">
                          {step === 3 && <span className="text-gray-400 animate-pulse">응답 생성 중...</span>}
                          {step >= 4 && <span className="text-gray-700">{botTyped2}</span>}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  // 실제 대화 모드
                  <>
                    {messages.map((msg, idx) => (
                      <div key={idx} className={`flex items-start mb-4 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                        {msg.role === 'bot' && (
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center mr-3 flex-shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                            </svg>
                          </div>
                        )}
                        <div className={`p-3 rounded-lg shadow-sm ${msg.role === 'bot' ? 'bg-white text-gray-700' : 'bg-primary/10 text-gray-700 min-w-[120px]'}`}>
                          {msg.content}
                        </div>
                        {msg.role === 'user' && (
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center ml-3 flex-shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
            {/* 장식용 그래픽 요소 */}
            <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-accent/20 rounded-full"></div>
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-secondary/20 rounded-full"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection; 